import { Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Else } from "lib/@components/else"
import { If } from "lib/@components/switch"
import { TabledRepeat } from "lib/@components/tabled-repeat/tabled-repeat"
import GenericSearch from "lib/@components/paging/generic-search"
import { useEffect, useState } from "react"
import { ExtendedNoResults } from "./extended-no-results"
import { ReferenceRow } from "./reference-row"
import { ResetTabledRepeat } from "event-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { SelectAllCheckbox } from "./lib/select-all-checkbox"
import { useDialog } from "lib/@hooks/useDialog"
import { AuthoringDeleteDialog } from "../plugins/project-item/authoring-delete-dialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { deleteLegislationFromGroup } from "./controller/delete-legislation-from-group"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { useParams } from "react-router"
import { AddReferencesButton } from "../components/add-references-button"
import { SelectedItemsBanner } from "../components/selected-items-banner"
import { GroupStatus } from "authoring/lib/enum/group-status.mjs"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { isGroupValid } from "./lib/is-group-valid"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function References() {
    const { project, group } = useBoundContext()
    const { projectId } = useParams()
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const isValid = isGroupValid(group, { canEditReferences: true })
    const isReadOnly = group?.readOnly || !isValid
    const groupState = { ...(group ?? {}), readOnly: isReadOnly }
    const legislation = groupState?.legislationData ?? []
    const [search, setSearch] = useState("")
    const [selectedLegislation, setSelectedLegislation] = useState({})
    const filteredLegislation = legislation.filter((l) => l?.title?.toLowerCase()?.includes(search?.toLowerCase()))
    const selectedLegislationIds = Object.keys(selectedLegislation).map(Number)
    const bannerSelected = isReadOnly ? [] : selectedLegislationIds
    const { selectedReferencesString } = extractSelectedReferences({ legislation, selectedLegislationIds })
    const disableDelete = group.status === GroupStatus.APPLIED || isLockedToLegacy(project?.phase)
    const referencesHeader = canEditSchedule ? <SelectAllCheckbox disabled={groupState.readOnly} /> : null
    const addReferencesButton = canEditSchedule ? <AddReferencesButton /> : null
    const addReferencesEmptyButton = canEditSchedule ? <AddReferencesButton variant="outlined" /> : null
    const headers = [
        { title: referencesHeader },
        {
            title: (
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Reference
                </Typography>
            ),
        },
        { title: "" },
    ]

    const deleteLegislationDialog = useDialog(
        <AuthoringDeleteDialog
            value={selectedLegislationIds}
            title="Delete legislation"
            message={`Are you sure you want to remove the following legislation from the group?\n\n${selectedReferencesString}`}
            messageSx={{ whiteSpace: "pre-line" }}
        />
    )

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [search, group?.legislationData])

    useEffect(() => {
        if ((!isValid || !canEditSchedule) && Object.keys(selectedLegislation).length) {
            setSelectedLegislation({})
        }
    }, [canEditSchedule, isValid, selectedLegislation])

    return (
        <Bound
            group={groupState}
            projectGroup={groupState}
            items={legislation}
            filteredItems={filteredLegislation}
            selectedKey="legacyId"
            selectedItems={selectedLegislation}
            setSelectedItems={setSelectedLegislation}
        >
            <Stack>
                <Stack direction="row" sx={{ justifyContent: "space-between", mb: 3 }}>
                    {addReferencesButton}
                    <GenericSearch
                        results={legislation}
                        searchPlaceHolder="Search"
                        search={search}
                        setSearch={setSearch}
                        showResultCount={false}
                        size="small"
                    />
                </Stack>
                <If truthy={legislation?.length > 0}>
                    <If value={canEditSchedule}>
                        <SelectedItemsBanner
                            selected={bannerSelected}
                            label={"reference"}
                            disabled={disableDelete}
                            handleDelete={() =>
                                handleDelete({
                                    projectId,
                                    groupId: group?.id,
                                    selectedLegislationIds,
                                    deleteLegislationDialog,
                                })
                            }
                        />
                    </If>
                    <TabledRepeat
                        items={filteredLegislation}
                        alternating={false}
                        itemComponent={<ReferenceRow />}
                        itemPass="reference"
                        tableHeaders={headers}
                        headerStyles={{ backgroundColor: "background.paper" }}
                        paged={true}
                    />
                    <Else>
                        <ExtendedNoResults
                            message="The references when added will be applied in bulk accross all schedules in your group"
                            component={addReferencesEmptyButton}
                        />
                    </Else>
                </If>
            </Stack>
        </Bound>
    )
}

async function handleDelete({ projectId, groupId, selectedLegislationIds, deleteLegislationDialog }) {
    try {
        const confirmDelete = await deleteLegislationDialog()
        if (confirmDelete) {
            const deleted = await deleteLegislationFromGroup({
                projectId,
                groupId,
                legislationIds: selectedLegislationIds,
            })
            if (deleted) {
                successSnackbar("Legislation successfully removed from group")
            } else {
                errorSnackbar("Failed to remove legislation from group")
            }
        }
    } catch (e) {
        console.error(e)
        errorSnackbar("Failed to remove legislation from group")
    }
}

function extractSelectedReferences({ legislation, selectedLegislationIds }) {
    const selectedIdsSet = new Set(selectedLegislationIds)
    const selectedReferences = legislation.filter((l) => selectedIdsSet.has(parseInt(l?.legacyId))).map((l) => l.title)
    const selectedReferencesString = selectedReferences.reduce((acc, curr) => acc + `${curr}\n`, ``)

    return { selectedReferences, selectedReferencesString }
}
